import * as React from 'react'

import Button from '../../general/Button/Button'
import Text from '../../atoms/Text'
import { colors } from '@lmc/common/constants'
import './HeroCard.less'
import Icon from '../../atoms/Icon'
import IconType from '../../atoms/Icon/iconTypes'
import { HomeSource } from '@lmc/ts-common'

export type HeroCardProps = {
  backgroundColor?: string
  backgroundImage?: string
  bodyText: string
  cta: string
  icon?:
    | IconType.CarePlansHexagon
    | IconType.DocsHexagon
    | IconType.EmarHexagon
    | IconType.FamilyHexagon
    | IconType.GroupsHexagon
    | IconType.ProHexagon
    | IconType.HandoversHexagon
    | IconType.ConsentHexagon
  onClick: () => void
  subTitle: string
  title: string
  ctaDisabled?: boolean
  disabledText?: string
  findOutMoreURL: string
  source?: HomeSource
  customLinkText?: string
}

const renderLeftContainer = ({
  title,
  subTitle,
  bodyText,
  cta,
  findOutMoreURL,
  onClick,
  ctaDisabled = false,
  disabledText,
  source,
  customLinkText,
}: HeroCardProps) => {
  const isSelfServe = source === 'Self-serve'

  return (
    <div style={styles.leftContainer}>
      <div>
        <Text as="h1" style={styles.title}>
          {title}
        </Text>
      </div>
      <div>
        <Text as="h4" style={styles.text}>
          {subTitle}
        </Text>
        <div style={styles.bodyTextContainer}>
          <Text as="p1" style={styles.text}>
            {bodyText}
          </Text>
          <Text as="p1" style={styles.text}>
            <a
              target="_blank"
              href={findOutMoreURL}
              style={styles.findOutMoreLink}
              rel="noreferrer"
            >
              {customLinkText || 'View pricing here'}
              <Icon name="RightOutlined" style={styles.rightArrow} />
            </a>
          </Text>
        </div>
        {isSelfServe && (
          <div style={styles.ctaContainer}>
            <Button
              variant="default"
              onClick={onClick}
              disabled={ctaDisabled}
              data-testid="cta"
              data-cy="cta-btn"
            >
              {cta}
            </Button>
            {ctaDisabled && (
              <Text as="p1" style={styles.text}>
                {disabledText}
              </Text>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default function HeroCard({
  backgroundColor,
  backgroundImage,
  icon,
  title,
  subTitle,
  bodyText,
  cta,
  onClick,
  ctaDisabled = false,
  findOutMoreURL,
  disabledText,
  source,
  customLinkText,
}: HeroCardProps) {
  const containerStyle = {
    backgroundImage: `url(${backgroundImage})`,
    backgroundColor,
    ...styles.container,
  }

  return (
    <div style={containerStyle} id="container" data-cy='paywall-container'>
      {renderLeftContainer({
        title,
        subTitle,
        bodyText,
        cta,
        onClick,
        ctaDisabled,
        disabledText,
        findOutMoreURL,
        source,
        customLinkText,
      })}
      <div id="plan-logo-wrapper">
        <Icon customIcon={icon} height={200} width={183} />
      </div>
    </div>
  )
}

const styles = {
  container: {
    borderRadius: 25,
    backgroundSize: 'auto 100%',
    backgroundPositionX: 'right',
    backgroundPositionY: 'bottom',
    backgroundRepeat: 'no-repeat',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  bodyTextContainer: {
    display: 'flex',
    flexWrap: 'wrap' as const,
  },
  ctaContainer: { marginTop: 14 },
  findOutMoreLink: {
    color: colors.white,
    textDecoration: 'underline',
  },
  rightArrow: {
    fontSize: 10,
    marginLeft: 2,
  },
  leftContainer: {
    padding: 18,
    maxWidth: 610,
  },
  logoContainer: {
    margin: '0px auto',
  },
  text: {
    color: colors.white,
    marginRight: 6,
    fontWeight: 700,
    fontSize: 18,
  },
  title: {
    color: colors.white,
    marginRight: 6,
    fontWeight: 700,
    fontSize: 32,
  },
}
